import React, { useEffect, useState } from 'react';
import { useStateContext } from 'shared/contexts/ContextProvider';
import { employeeService } from 'features/company/api';
import { Loader2, Inbox, Bell, User, ChevronDown, ChevronUp } from 'lucide-react';

// ---------------------------------------------------------------------------
// Palette — matches the project's existing design system
// ---------------------------------------------------------------------------
const C = {
    bg: '#F3F5F8',
    card: '#FFFFFF',
    border: '#E4E8EF',
    text: '#1B2430',
    textSoft: '#6B7686',
    textFaint: '#9AA4B2',
    primary: '#3457D5',
    primarySoft: '#EAEFFC',
    unread: '#3457D5',
    unreadBg: '#F0F4FF',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const DESCRIPTION_LIMIT = 200; // characters before "Read more" kicks in

function formatDate(isoString) {
    if (!isoString) return null;
    const d = new Date(isoString);
    return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

// ---------------------------------------------------------------------------
// NotificationCard
// ---------------------------------------------------------------------------
function NotificationCard({ notification }) {
    const [expanded, setExpanded] = useState(false);

    const { title, description, tag, employee, is_read, sent_at, read_at } = notification;

    const tagColor = tag
        ? `rgb(${tag.red}, ${tag.green}, ${tag.blue})`
        : C.primary;
    const tagBg = tag
        ? `rgba(${tag.red}, ${tag.green}, ${tag.blue}, 0.12)`
        : C.primarySoft;

    const isLong = description && description.length > DESCRIPTION_LIMIT;
    const displayText =
        isLong && !expanded
            ? description.slice(0, DESCRIPTION_LIMIT).trimEnd() + '…'
            : description;

    const dateStr = formatDate(sent_at || read_at);

    return (
        <div style={{
            background: is_read ? C.card : C.unreadBg,
            border: `1px solid ${is_read ? C.border : '#C8D6F8'}`,
            borderRadius: 12,
            padding: '18px 20px',
            display: 'flex',
            gap: 16,
            position: 'relative',
            transition: 'box-shadow 0.18s ease',
        }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 18px rgba(52,87,213,0.10)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
        >
            {/* Unread dot */}
            {!is_read && (
                <span style={{
                    position: 'absolute',
                    top: 18,
                    right: 18,
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: C.unread,
                    boxShadow: `0 0 0 3px rgba(52,87,213,0.18)`,
                }} />
            )}

            {/* Avatar / icon */}
            <div style={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: tagBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Bell size={20} color={tagColor} strokeWidth={1.8} />
            </div>

            {/* Body */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Top row: tag + date */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    {tag && (
                        <span style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.3,
                            color: tagColor,
                            background: tagBg,
                            borderRadius: 20,
                            padding: '2px 10px',
                            border: `1px solid ${tagColor}30`,
                        }}>
                            {tag.text}
                        </span>
                    )}
                    {dateStr && (
                        <span style={{ fontSize: 12, color: C.textFaint, marginLeft: 'auto' }}>
                            {dateStr}
                        </span>
                    )}
                </div>

                {/* Title */}
                <p style={{
                    margin: '0 0 4px',
                    fontSize: 14,
                    fontWeight: is_read ? 500 : 700,
                    color: C.text,
                    lineHeight: 1.4,
                }}>
                    {title}
                </p>

                {/* Employee name */}
                {employee?.meta?.name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                        <User size={12} color={C.textFaint} />
                        <span style={{ fontSize: 12, color: C.textSoft }}>
                            {employee.meta.name}
                        </span>
                    </div>
                )}

                {/* Description */}
                {description && (
                    <div>
                        <p style={{
                            margin: '0 0 4px',
                            fontSize: 13,
                            color: C.textSoft,
                            lineHeight: 1.65,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}>
                            {displayText}
                        </p>
                        {isLong && (
                            <button
                                onClick={() => setExpanded(v => !v)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    color: C.primary,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 3,
                                }}
                            >
                                {expanded ? (
                                    <><ChevronUp size={14} /> Свернуть</>
                                ) : (
                                    <><ChevronDown size={14} /> Читать далее</>
                                )}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ConsultantNotifications() {
    const { showToast, token } = useStateContext();
    const [notifications, setNotifications] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const data = await employeeService.getNotifications('', token);
            if (data.status !== 0) {
                showToast({
                    title: 'Error!',
                    content: data.message || 'Failed to load notifications.',
                    cssClass: 'e-toast-danger',
                    icon: 'e-error toast-icons',
                });
                return;
            }
            // Mark all as read on the server, then update local state optimistically
            const rows = data.response?.rows ?? [];
            if (rows.some(n => !n.is_read)) {
                employeeService.readAllNotifications(token).catch(() => {
                    // silent — read-status update is best-effort
                });
            }
            setNotifications({
                ...data.response,
                rows: rows.map(n => ({ ...n, is_read: true })),
            });
        } catch (error) {
            showToast({
                title: 'Error!',
                content: 'Failed to load notifications.',
                cssClass: 'e-toast-danger',
                icon: 'e-error toast-icons',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNotifications();
    }, [token]);

    const rows = notifications?.rows ?? [];
    const unreadCount = rows.filter(n => !n.is_read).length;

    // -----------------------------------------------------------------------
    // Loading
    // -----------------------------------------------------------------------
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 320,
                gap: 12,
                color: C.textFaint,
            }}>
                <Loader2 size={32} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 14 }}>Загрузка уведомлений…</span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Empty
    // -----------------------------------------------------------------------
    if (!rows.length) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 320,
                gap: 12,
                color: C.textFaint,
            }}>
                <Inbox size={48} strokeWidth={1.2} color={C.textFaint} />
                <p style={{ margin: 0, fontSize: 15, fontWeight: 500, color: C.textSoft }}>Нет уведомлений</p>
                <p style={{ margin: 0, fontSize: 13 }}>Новые уведомления появятся здесь</p>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // List
    // -----------------------------------------------------------------------
    return (
        <div style={{ padding: '24px 0', fontFamily: 'Inter, system-ui, sans-serif' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 20,
            }}>
                <Bell size={20} color={C.primary} />
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>
                    Уведомления
                </h2>
                {unreadCount > 0 && (
                    <span style={{
                        background: C.primary,
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        borderRadius: 20,
                        padding: '1px 9px',
                        marginLeft: 4,
                    }}>
                        {unreadCount} новых
                    </span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 13, color: C.textFaint }}>
                    Всего: {rows.length}
                </span>
            </div>

            {/* Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {rows.map(notification => (
                    <NotificationCard key={notification.id} notification={notification} />
                ))}
            </div>
        </div>
    );
}